import { Events, IDeleteEventsOnDates, IDeleteEventsInPeriod } from "../models/events";
import { format, isSameDay } from "date-fns";
import { errorMessage } from "../utils/errorMessageUtils";
import i18n from "../i18n";
import store from "../store";
import { extendArray } from "./types";
import { confirmDialog } from "@/composables/useConfirmDialog";

export default class EventsHandler {
  static deleteEventConfirmationText(event: Events, dates: Date[] = []): string {
    let eventDate = format(event.start_time, "dd/MM-yyyy");
    if (dates.length > 0) {
      eventDate = "";
      dates.forEach((d: Date) => {
        eventDate += format(d, "dd/MM-yyyy") + "<br>";
      });
    }
    return i18n.global
      .t("EventsHandler.deleteEventConfirmationText", {
        eventname: event.name,
        eventdate: eventDate
      })
      .toString();
  }

  static returnAnyDeleteWarnings(deleteWarningsList: any): string {
    if (deleteWarningsList?.data?.length > 0) {
      return deleteWarningsList.data.join("<br>");
    }
    return "";
  }

  static returnCouldNotDeleteEventMessage(error: any): string {
    return errorMessage(error, i18n.global.t("eventdialog.couldNotDeleteEvent").toString());
  }

  static async deleteRecurrentEvents(EventId: number): Promise<string> {
    return await store
      .dispatch("events/deleteRecurrentEvents", EventId)
      .then((deleteWarningsList: any) => EventsHandler.returnAnyDeleteWarnings(deleteWarningsList))
      .catch((error: any) => EventsHandler.returnCouldNotDeleteEventMessage(error));
  }

  static async deleteRecurrentEventsOnDates(deleteEvents: IDeleteEventsOnDates): Promise<string> {
    return await store
      .dispatch("events/deleteEventsOnDates", deleteEvents)
      .then((deleteWarningsList: any) => EventsHandler.returnAnyDeleteWarnings(deleteWarningsList))
      .catch((error: any) => EventsHandler.returnCouldNotDeleteEventMessage(error));
  }

  static async deleteRecurrentEventsInPeriod(deleteEvents: IDeleteEventsInPeriod): Promise<string> {
    return await store
      .dispatch("events/deleteEventsInPeriod", deleteEvents)
      .then((deleteWarningsList: any) => EventsHandler.returnAnyDeleteWarnings(deleteWarningsList))
      .catch((error: any) => EventsHandler.returnCouldNotDeleteEventMessage(error));
  }

  static async deleteEvents(EventId: number): Promise<string> {
    return await store
      .dispatch("events/deleteEvents", EventId)
      .then((deleteWarningsList: any) => EventsHandler.returnAnyDeleteWarnings(deleteWarningsList))
      .catch((error: any) => EventsHandler.returnCouldNotDeleteEventMessage(error));
  }

  static showEventWarning() {
    confirmDialog.show({
      title: i18n.global.t("warnings.NoEditOthersActivities").toString(),
      message: i18n.global.t("warnings.NoEditOthersActivitiesText").toString(),
      html: true,
      ok: true,
      cancel: false
    });
  }
}

@extendArray
export class EventArray extends Array<Events> {
  constructor(events: Events[] | number) {
    if (events instanceof Array) {
      super(...events);
    } else {
      super(events);
    }
    Object.setPrototypeOf(this, Object.create(EventArray.prototype));
  }

  isClosed(date: Date): boolean {
    const eventtypeID = store.getters["events/GetCloseDayEventypeID"];
    return this.some((e) => isSameDay(e.start_time, date) && e.event_type_id === eventtypeID);
  }

  isStudentFreeDate(date: Date): boolean {
    const eventtypeID = store.getters["events/GetPupilFreeID"];
    return this.some((e) => isSameDay(e.start_time, date) && e.event_type_id === eventtypeID);
  }

  onDate(date: Date): EventArray {
    return new EventArray(this.filter((a) => isSameDay(a.start_time, date)));
  }

  onDateActive(date: Date): EventArray {
    const eventtypeID = store.getters["events/GetCloseDayEventypeID"];
    const eventPupilFreeID = store.getters["events/GetPupilFreeID"];
    return new EventArray(
      this.filter(
        (a) =>
          isSameDay(a.start_time, date) &&
          a.event_type_id !== eventtypeID &&
          a.event_type_id !== eventPupilFreeID
      )
    );
  }

  getClosedDayEvent(date: Date): Events | undefined {
    const eventtypeID = store.getters["events/GetCloseDayEventypeID"];
    return this.find((e) => isSameDay(e.start_time, date) && e.event_type_id === eventtypeID);
  }

  getPupilFreeEvent(date: Date): Events | undefined {
    const eventtypeID = store.getters["events/GetPupilFreeID"];
    return this.find((e) => isSameDay(e.start_time, date) && e.event_type_id === eventtypeID);
  }
}
